import { useEffect, useRef } from 'react'
import type { GeoJSONSource, LngLatBoundsLike, Map } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { ECUADOR_CENTER, ECUADOR_INITIAL_ZOOM, getMapStyle } from '@/lib/map/mapConfig'
import { trackEvent } from '@/analytics'

export type VotingMapFocusRequest =
  | { type: 'bounds'; key: string }
  | { type: 'recinto'; recintoId: string; key: string }

interface Props {
  geojson: GeoJSON.FeatureCollection | null
  selectedRecintoId: string | null
  focusRequest: VotingMapFocusRequest | null
  onSelectRecinto: (id: string) => void
}

function getPointCoordinates(feature: GeoJSON.Feature): [number, number] | null {
  if (feature.geometry?.type !== 'Point') return null

  const [lng, lat] = feature.geometry.coordinates
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null

  return [lng, lat]
}

function getFeatureId(feature: GeoJSON.Feature) {
  return String(feature.id ?? feature.properties?.id ?? '')
}

function getGeoJsonBounds(geojson: GeoJSON.FeatureCollection): LngLatBoundsLike | null {
  let west = Number.POSITIVE_INFINITY
  let south = Number.POSITIVE_INFINITY
  let east = Number.NEGATIVE_INFINITY
  let north = Number.NEGATIVE_INFINITY
  let points = 0

  for (const feature of geojson.features) {
    const coordinates = getPointCoordinates(feature)
    if (!coordinates) continue

    const [lng, lat] = coordinates
    west = Math.min(west, lng)
    south = Math.min(south, lat)
    east = Math.max(east, lng)
    north = Math.max(north, lat)
    points += 1
  }

  if (!points) return null

  if (west === east && south === north) {
    const offset = 0.01
    return [
      [west - offset, south - offset],
      [east + offset, north + offset],
    ]
  }

  return [
    [west, south],
    [east, north],
  ]
}

function findRecintoCoordinates(
  geojson: GeoJSON.FeatureCollection,
  recintoId: string,
): [number, number] | null {
  const feature = geojson.features.find((item) => getFeatureId(item) === recintoId)
  return feature ? getPointCoordinates(feature) : null
}

export function VotingPlaceMap({
  focusRequest,
  geojson,
  selectedRecintoId,
  onSelectRecinto,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<Map | null>(null)
  const onSelectRef = useRef(onSelectRecinto)
  const pendingSelectionRef = useRef(selectedRecintoId)
  const pendingFocusRef = useRef(focusRequest)
  const lastFocusKeyRef = useRef<string | null>(null)
  const latestGeoJsonRef = useRef(geojson)
  const statusRef = useRef<HTMLParagraphElement | null>(null)

  latestGeoJsonRef.current = geojson

  function applyPendingFocus() {
    const map = mapRef.current
    const currentGeoJson = latestGeoJsonRef.current
    const currentFocus = pendingFocusRef.current

    if (!map?.isStyleLoaded() || !currentGeoJson || !currentFocus) return
    if (!map.getSource('recintos')) return
    if (lastFocusKeyRef.current === currentFocus.key) return

    if (currentFocus.type === 'recinto') {
      const coordinates = findRecintoCoordinates(currentGeoJson, currentFocus.recintoId)
      if (!coordinates) return

      map.easeTo({ center: coordinates, zoom: 16, duration: 900 })
      lastFocusKeyRef.current = currentFocus.key
      return
    }

    const bounds = getGeoJsonBounds(currentGeoJson)
    if (!bounds) return

    map.fitBounds(bounds, { padding: 48, maxZoom: 13, duration: 900 })
    lastFocusKeyRef.current = currentFocus.key
  }

  useEffect(() => {
    onSelectRef.current = onSelectRecinto
  }, [onSelectRecinto])

  useEffect(() => {
    pendingFocusRef.current = focusRequest
  }, [focusRequest])

  useEffect(() => {
    if (!containerRef.current || mapRef.current || !geojson) return

    let isMounted = true
    const initialGeoJson = geojson

    async function setupMap() {
      const maplibre = await import('maplibre-gl')
      if (!isMounted || !containerRef.current || mapRef.current) return

      let style
      try {
        style = getMapStyle()
      } catch (error) {
        if (statusRef.current) {
          statusRef.current.textContent =
            error instanceof Error ? error.message : 'No se pudo configurar el mapa.'
        }
        return
      }

      const map = new maplibre.Map({
        container: containerRef.current,
        style,
        center: ECUADOR_CENTER,
        zoom: ECUADOR_INITIAL_ZOOM,
        attributionControl: { compact: true },
      })

      map.addControl(new maplibre.NavigationControl(), 'top-right')

      map.on('load', () => {
        map.addSource('recintos', {
          type: 'geojson',
          data: initialGeoJson,
          cluster: true,
          clusterMaxZoom: 13,
          clusterRadius: 48,
          promoteId: 'id',
        })

        map.addLayer({
          id: 'recintos-clusters',
          type: 'circle',
          source: 'recintos',
          filter: ['has', 'point_count'],
          paint: {
            'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 30],
            'circle-color': '#1267D9',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          },
        })

        map.addLayer({
          id: 'recintos-cluster-count',
          type: 'symbol',
          source: 'recintos',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 12,
          },
          paint: {
            'text-color': '#ffffff',
          },
        })

        map.addLayer({
          id: 'recintos-points',
          type: 'circle',
          source: 'recintos',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-radius': 6,
            'circle-color': '#080745',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          },
        })

        map.addLayer({
          id: 'recintos-selected',
          type: 'circle',
          source: 'recintos',
          filter: ['==', ['get', 'id'], pendingSelectionRef.current ?? ''],
          paint: {
            'circle-radius': 10,
            'circle-color': '#FFD400',
            'circle-stroke-color': '#080745',
            'circle-stroke-width': 3,
          },
        })

        requestAnimationFrame(applyPendingFocus)

        map.on('click', 'recintos-clusters', async (event) => {
          const feature = map.queryRenderedFeatures(event.point, {
            layers: ['recintos-clusters'],
          })[0]

          const clusterId = Number(feature?.properties?.cluster_id)
          if (!Number.isFinite(clusterId)) return

          const source = map.getSource('recintos') as GeoJSONSource
          const zoom = await source.getClusterExpansionZoom(clusterId)
          const coordinates = (feature.geometry as GeoJSON.Point).coordinates as [number, number]
          map.easeTo({ center: coordinates, zoom })
          trackEvent('voting_map_cluster_expand', {
            event_category: 'where_to_vote',
            point_count: Number(feature?.properties?.point_count) || undefined,
            expansion_zoom: Math.round(zoom),
          })
        })

        map.on('click', 'recintos-points', (event) => {
          const feature = event.features?.[0]
          const id = String(feature?.properties?.id ?? feature?.id ?? '')
          if (id) onSelectRef.current(id)
        })

        for (const layerId of ['recintos-clusters', 'recintos-points']) {
          map.on('mouseenter', layerId, () => {
            map.getCanvas().style.cursor = 'pointer'
          })
          map.on('mouseleave', layerId, () => {
            map.getCanvas().style.cursor = ''
          })
        }
      })

      map.on('error', () => {
        if (statusRef.current) statusRef.current.textContent = 'No se pudo cargar el mapa.'
      })
      map.on('idle', applyPendingFocus)

      mapRef.current = map
    }

    setupMap()

    return () => {
      isMounted = false
      mapRef.current?.off('idle', applyPendingFocus)
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [Boolean(geojson)])

  useEffect(() => {
    pendingSelectionRef.current = selectedRecintoId
    const map = mapRef.current
    if (!map?.isStyleLoaded()) return
    if (!map.getLayer('recintos-selected')) return

    map.setFilter('recintos-selected', [
      '==',
      ['get', 'id'],
      selectedRecintoId ?? '',
    ])
  }, [selectedRecintoId])

  useEffect(() => {
    const source = mapRef.current?.getSource('recintos') as GeoJSONSource | undefined
    if (source && geojson) {
      source.setData(geojson)
      requestAnimationFrame(applyPendingFocus)
    }
  }, [geojson])

  useEffect(() => {
    pendingFocusRef.current = focusRequest
    applyPendingFocus()
  }, [focusRequest, geojson])

  return (
    <div className="voting-map" aria-label="Mapa de recintos electorales">
      {!geojson ? <p className="voting-map__status">Cargando mapa...</p> : null}
      <p ref={statusRef} className="voting-map__status" aria-live="polite" />
      <div ref={containerRef} className="voting-map__canvas" />
    </div>
  )
}
